import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import { useOrderByHashQuery } from '@/services/orders'
import { useProductByHashQuery, useProductVariationQuery } from '@/services/products'
import { colors } from '@/theme'
import type { MedicationRequest } from '@/services/med-req-detail'
import { Box, Divider, Grid, Link, Paper, Typography } from '@mui/material'

interface PatientDataProps {
  medRequest: MedicationRequest
}

function Field({
  label,
  value,
  asUrl,
  size = 3
}: {
  label: string
  value?: string | null
  asUrl?: string
  size?: number
}) {
  const displayValue = value || '-'

  return (
    <Grid size={size}>
      <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>{label}</Typography>
      {asUrl ? (
        <Link
          href={asUrl}
          target='_blank'
          rel='noopener noreferrer'
          underline='hover'
          sx={{
            color: colors.brand.primary,
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'block',
            overflow: 'visible',
            textOverflow: 'unset',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word'
          }}
        >
          {displayValue}
        </Link>
      ) : (
        <Typography
          sx={{
            color: colors.text.dark,
            fontSize: '14px',
            fontWeight: 400
          }}
        >
          {displayValue}
        </Typography>
      )}
    </Grid>
  )
}

const FieldRow = ({ children }: { children: React.ReactNode }) => (
  <Grid
    container
    sx={{
      '& > *:not(:last-child)': {
        borderRight: `1px solid ${colors.border.default}`,
        pr: 3
      },
      '& > *:not(:first-child)': { pl: 3 }
    }}
  >
    {children}
  </Grid>
)

function OrderedItemFields({ medRequest }: { medRequest: MedicationRequest }) {
  const variationHash = medRequest.orderedItem.originalProductVariationHash
  const productHash = medRequest.orderedItem.originalProductHash

  const { data: variationProduct, isLoading: isVariationLoading } = useProductVariationQuery(variationHash || undefined)
  const { data: productByHash, isLoading: isProductLoading } = useProductByHashQuery(
    variationHash ? undefined : productHash || undefined
  )

  const product = variationProduct ?? productByHash
  const isLoading = isVariationLoading || isProductLoading

  if (isLoading) {
    return (
      <Grid size={9}>
        <InlineLoadingSpinner />
      </Grid>
    )
  }

  const orderedVariation = variationHash ? product?.variations?.find(v => v.hash === variationHash) : undefined
  const orderedDose = orderedVariation?.strength || orderedVariation?.pa_ep_strength?.label || '-'
  const orderedQuantity = orderedVariation?.quantity || '-'

  return (
    <>
      <Field
        label='Ordered item'
        value={orderedVariation?.title || product?.title || '-'}
        asUrl={variationHash ? `/products/${variationHash}` : undefined}
      />
      <Field label='Ordered dose' value={orderedDose} />
      <Field label='Ordered quantity' value={orderedQuantity} />
    </>
  )
}

function OrderAndShippingSection({ medRequest }: { medRequest: MedicationRequest }) {
  const { data: order } = useOrderByHashQuery(medRequest.orderHash)
  const shippingCountry = order?.shipping.country || medRequest.orderCountry

  return (
    <Box>
      <Typography
        sx={{
          color: colors.text.dark,
          fontSize: '16px',
          fontWeight: 600,
          mb: 3
        }}
      >
        Order
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FieldRow>
          <Field
            label='User name'
            value={`${order?.billing?.firstName ?? ''} ${order?.billing?.lastName ?? ''}` || order?.userDisplayName}
            asUrl={`/users/${encodeURIComponent(medRequest.customerEmail)}`}
          />
          <Field label='Order number' value={medRequest.orderNumber} asUrl={`/orders/${medRequest.orderHash}`} />
          <Field label='Order type' value={medRequest?.productGroup?.toUpperCase()} />
        </FieldRow>

        <FieldRow>
          <Field label='Card holder first name' value={order.billing.firstName ?? '-'} />
          <Field label='Card holder last name' value={order.billing.lastName ?? '-'} />
          <Field label='Cost' value={`£${order.totalAmount}`} />
          <Field label='Number of items to dispense' value={order.itemCount?.toString() ?? '-'} />
        </FieldRow>

        <FieldRow>
          <Field label='Shipping First Name' value={order.shipping.firstName} />
          <Field label='Shipping Last Name' value={order.shipping.lastName} />
          <Field label='Shipping Address 1' value={order.shipping.address1} />
          <Field label='Shipping Address 2' value={order.shipping.address2} />
        </FieldRow>

        <FieldRow>
          <Field label='Shipping City' value={order.shipping.city} />
          <Field label='Shipping Postcode' value={order.shipping.postCode} />
          <Field label='Shipping Country' value={shippingCountry} />
          <Field label='Shipping Type' value={order.shippingMethod} />
        </FieldRow>
      </Box>
    </Box>
  )
}

export default function PatientData({ medRequest }: PatientDataProps) {
  const patient = medRequest?.patient
  const patientName = `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim()

  return (
    <Paper
      id='patient-details'
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${colors.border.default}`,
        bgcolor: colors.background.paper,
        overflow: 'hidden',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
    >
      {/* Patient Details Header */}
      <Box>
        <Typography
          sx={{
            color: colors.text.dark,
            fontSize: '16px',
            fontWeight: 600,
            mb: 3
          }}
        >
          Patient details
        </Typography>

        <FieldRow>
          <Field label='Patient name' value={patientName || 'Unknown'} asUrl={`/patients/${patient.id}`} />
          <Field label='Gender' value={patient?.gender} />
          <Field label='Date of birth' value={patient?.dob} />
          <Field label='Post code' value={medRequest?.orderPostCode} />
        </FieldRow>
      </Box>

      <Divider />

      {/* Medication Request Section */}
      <Box>
        <Typography
          sx={{
            color: colors.text.dark,
            fontSize: '16px',
            fontWeight: 600,
            mb: 3
          }}
        >
          Medication request
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FieldRow>
            <OrderedItemFields medRequest={medRequest} />
          </FieldRow>

          <FieldRow>
            <Field
              label='Prescribed item'
              value={medRequest.prescribedItemName || medRequest.orderedItem.name || '-'}
            />
            <Field label='Prescribed dose' value={medRequest.orderedItem.dose || '-'} />
            <Field label='Prescribed quantity' value={medRequest.orderedItem.quantity || '-'} />
          </FieldRow>

          <FieldRow>
            <Field
              label='GP'
              value={medRequest.gp?.name}
              asUrl={medRequest.gp ? `/gps/${medRequest.gp.hash}` : undefined}
            />
            <Field
              label='Prescription url'
              value={medRequest.prescriptionUrl}
              asUrl={medRequest.prescriptionUrl ?? undefined}
            />
            <Field label='Closing date' value={medRequest.closingDate} />
          </FieldRow>
          <FieldRow>
            <Field label='Directions' value={medRequest.orderedItem?.directions || '-'} size={12} />
          </FieldRow>
        </Box>
      </Box>

      <Divider />

      {/* Order and Shipping Section */}
      <OrderAndShippingSection medRequest={medRequest} />
    </Paper>
  )
}
